import React from "react";
import { MetricLabel } from "../../MetricCards";
import panelStyles from "../../styles/Panel.module.css";
import analyticsStyles from "../../styles/Analytics.module.css";
import {
  formatNumber,
  formatPercent,
  formatSignedPercent
} from "../../utils/formatters";
import {
  formatAhaLag,
  formatAhaLagBuckets
} from "../../utils/analyticsFormatters";

function renderRow(title, hint, day, week, month) {
  return { title, hint, day, week, month };
}

export function AnalyticsFunnelBlock({ day, week, month }) {
  const rows = [
    renderRow(
      "Новые регистрации",
      "Когортная метрика: пользователи с regDate в периоде; в скобках изменение к предыдущему периоду.",
      `${formatNumber(day.newUsers)} (${formatSignedPercent(day.newUsersDeltaPct)})`,
      `${formatNumber(week.newUsers)} (${formatSignedPercent(week.newUsersDeltaPct)})`,
      `${formatNumber(month.newUsers)} (${formatSignedPercent(month.newUsersDeltaPct)})`
    ),
    renderRow(
      "Подписка на группу",
      "Из новых пользователей, у кого есть событие group_join.",
      `${formatNumber(day.joined)} (${formatPercent(day.joinRate)})`,
      `${formatNumber(week.joined)} (${formatPercent(week.joinRate)})`,
      `${formatNumber(month.joined)} (${formatPercent(month.joinRate)})`
    ),
    renderRow(
      "Активировались (1+ запрос)",
      "Сделали хотя бы один запрос.",
      `${formatNumber(day.activation.activated)} (${formatPercent(day.activation.rate)})`,
      `${formatNumber(week.activation.activated)} (${formatPercent(week.activation.rate)})`,
      `${formatNumber(month.activation.activated)} (${formatPercent(month.activation.rate)})`
    ),
    renderRow(
      "Активировались за 7 дней",
      "Сделали 1+ запрос в течение 7 дней после регистрации.",
      `${formatNumber(day.activation.activated7)} (${formatPercent(day.activation.rate7)})`,
      `${formatNumber(week.activation.activated7)} (${formatPercent(week.activation.rate7)})`,
      `${formatNumber(month.activation.activated7)} (${formatPercent(month.activation.rate7)})`
    ),
    renderRow(
      "Конверсия от подписавшихся",
      "Активировались / Подписались на группу.",
      formatPercent(day.activation.fromJoinedRate),
      formatPercent(week.activation.fromJoinedRate),
      formatPercent(month.activation.fromJoinedRate)
    ),
    renderRow(
      "Время до 1-го запроса (среднее / медиана, мин)",
      "Среднее и медиана времени от регистрации до первого запроса (в минутах).",
      `${formatNumber(day.activation.timeAvgMin)} / ${formatNumber(day.activation.timeMedianMin)}`,
      `${formatNumber(week.activation.timeAvgMin)} / ${formatNumber(week.activation.timeMedianMin)}`,
      `${formatNumber(month.activation.timeAvgMin)} / ${formatNumber(month.activation.timeMedianMin)}`
    ),
    renderRow(
      "Время до 1-го запроса (P50 / P75 / P90)",
      "Перцентили времени от регистрации до первого запроса (в минутах).",
      `${formatNumber(day.activation.p50)} / ${formatNumber(day.activation.p75)} / ${formatNumber(day.activation.p90)}`,
      `${formatNumber(week.activation.p50)} / ${formatNumber(week.activation.p75)} / ${formatNumber(week.activation.p90)}`,
      `${formatNumber(month.activation.p50)} / ${formatNumber(month.activation.p75)} / ${formatNumber(month.activation.p90)}`
    ),
    renderRow(
      "Aha (>=500 токенов/24ч)",
      "Если токены не пишутся, используем оценку по числу запросов.",
      `${formatNumber(day.aha.users)} (${formatPercent(day.aha.rateFromActivated)})`,
      `${formatNumber(week.aha.users)} (${formatPercent(week.aha.rateFromActivated)})`,
      `${formatNumber(month.aha.users)} (${formatPercent(month.aha.rateFromActivated)})`
    ),
    renderRow(
      "Среднее запросов в 1-й день",
      "Кол-во запросов в первые 24ч / новые пользователи.",
      formatNumber(day.aha.avgRequestsDay1),
      formatNumber(week.aha.avgRequestsDay1),
      formatNumber(month.aha.avgRequestsDay1)
    ),
    renderRow(
      "Доля не дошедших до Aha",
      "1 - (Aha / Активировались).",
      formatPercent(day.aha.noAhaRate),
      formatPercent(week.aha.noAhaRate),
      formatPercent(month.aha.noAhaRate)
    ),
    renderRow(
      "Смотрели тарифы",
      "Пользователи с событием paywall_view; доля от Aha.",
      `${formatNumber(day.paywall.viewUsers)} (${formatPercent(day.paywall.rateFromAha)})`,
      `${formatNumber(week.paywall.viewUsers)} (${formatPercent(week.paywall.rateFromAha)})`,
      `${formatNumber(month.paywall.viewUsers)} (${formatPercent(month.paywall.rateFromAha)})`
    ),
    renderRow(
      "Начали оплату (checkout_open)",
      "Доля от paywall_view.",
      `${formatNumber(day.checkout.openUsers)} (${formatPercent(day.checkout.rateFromPaywall)})`,
      `${formatNumber(week.checkout.openUsers)} (${formatPercent(week.checkout.rateFromPaywall)})`,
      `${formatNumber(month.checkout.openUsers)} (${formatPercent(month.checkout.rateFromPaywall)})`
    ),
    renderRow(
      "Просмотры до покупки (avg / median)",
      "Количество paywall_view до первой покупки.",
      `${formatNumber(day.paywall.avgViewsBeforePurchase)} / ${formatNumber(day.paywall.medianViewsBeforePurchase)}`,
      `${formatNumber(week.paywall.avgViewsBeforePurchase)} / ${formatNumber(week.paywall.medianViewsBeforePurchase)}`,
      `${formatNumber(month.paywall.avgViewsBeforePurchase)} / ${formatNumber(month.paywall.medianViewsBeforePurchase)}`
    ),
    renderRow(
      "Распределение просмотров (1 / 2-3 / 4+)",
      "Сколько покупок произошло после 1 / 2-3 / 4+ просмотров.",
      `${formatNumber(day.paywall.dist.one)} / ${formatNumber(day.paywall.dist.twoThree)} / ${formatNumber(day.paywall.dist.fourPlus)}`,
      `${formatNumber(week.paywall.dist.one)} / ${formatNumber(week.paywall.dist.twoThree)} / ${formatNumber(week.paywall.dist.fourPlus)}`,
      `${formatNumber(month.paywall.dist.one)} / ${formatNumber(month.paywall.dist.twoThree)} / ${formatNumber(month.paywall.dist.fourPlus)}`
    ),
    renderRow(
      "Купили (уник.)",
      "CR = покупка / просмотры тарифов.",
      `${formatNumber(day.purchase.users)} (${formatPercent(day.purchase.crPaywallPurchase)})`,
      `${formatNumber(week.purchase.users)} (${formatPercent(week.purchase.crPaywallPurchase)})`,
      `${formatNumber(month.purchase.users)} (${formatPercent(month.purchase.crPaywallPurchase)})`
    ),
    renderRow(
      "CR checkout → покупка",
      "Покупка / checkout_open.",
      formatPercent(day.purchase.crCheckoutPurchase),
      formatPercent(week.purchase.crCheckoutPurchase),
      formatPercent(month.purchase.crCheckoutPurchase)
    ),
    renderRow(
      "CR Aha → покупка",
      "Покупки / Aha.",
      formatPercent(day.purchase.crAhaPurchase),
      formatPercent(week.purchase.crAhaPurchase),
      formatPercent(month.purchase.crAhaPurchase)
    ),
    renderRow(
      "Покупки с Aha / без Aha",
      "Покупатели с зафиксированным Aha до покупки.",
      `${formatNumber(day.purchase.usersWithAha)} / ${formatNumber(day.purchase.usersWithoutAha)}`,
      `${formatNumber(week.purchase.usersWithAha)} / ${formatNumber(week.purchase.usersWithoutAha)}`,
      `${formatNumber(month.purchase.usersWithAha)} / ${formatNumber(month.purchase.usersWithoutAha)}`
    ),
    renderRow(
      "Задержка Aha → покупка (среднее / медиана, дней)",
      "Если нет выборки, показывается —.",
      formatAhaLag(day.purchase),
      formatAhaLag(week.purchase),
      formatAhaLag(month.purchase)
    ),
    renderRow(
      "% покупок в день 0 / 1 / 3 / 7",
      "Накопительно после Aha.",
      formatAhaLagBuckets(day.purchase),
      formatAhaLagBuckets(week.purchase),
      formatAhaLagBuckets(month.purchase)
    )
  ];

  return (
    <div className={analyticsStyles.block}>
      <h3>Когортная воронка новых пользователей</h3>
      <p className={panelStyles.note}>
        Колонки День / Неделя / Месяц здесь показывают когорты пользователей по дате регистрации, а не последние 24ч / 7д / 30д событий.
      </p>
      <div className={panelStyles.tableWrap}>
        <table className={panelStyles.table}>
          <thead>
            <tr>
              <th>Метрика</th>
              <th>День</th>
              <th>Неделя</th>
              <th>Месяц</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.title}>
                <td>
                  <MetricLabel title={row.title} hint={row.hint} />
                </td>
                <td>{row.day}</td>
                <td>{row.week}</td>
                <td>{row.month}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default AnalyticsFunnelBlock;
